import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

// Combine tailwind classes without conflicts.
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function toTitleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map((word: string) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

function capitalizeFirstLetter(name: string) {
  return `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
}

function numberToFixed(value: number, decimals = 2) {
  return Number.parseFloat(value.toFixed(decimals).replaceAll('.00', ''));
}

function formatCurrency(value: string | number) {
  const floatValue = Number.parseFloat(value.toString());
  const hasDecimals = floatValue % 1 !== 0;

  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(floatValue);
}

/**
 * Contains default classes to apply to links,
 * which includes 'disabled' and 'focus' states.
 */
function getDefaultLinkClasses(nested?: boolean) {
  if (nested) {
    return cn(
      // When links are nested inline within regular text content.
      '[&_a]:cursor-pointer [&_a]:outline-0 [&_a]:outline-offset-2',
      '[&_a]:disabled:pointer-events-none [&_a]:disabled:opacity-50',
      '[&_a]:text-inherit [&_a]:inline [&_a]:underline [&_a]:underline-offset-2',
      '[&_a]:focus-visible:outline-2 [&_a]:focus-visible:outline-tertiary-foreground-hover',
    );
  }

  return cn(
    'cursor-pointer outline-0 outline-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-2 focus-visible:outline-tertiary-foreground-hover',
  );
}

function generateUniqueIdentifierId() {
  return uuidv4();
}

export {
  capitalizeFirstLetter,
  cn,
  formatCurrency,
  generateUniqueIdentifierId,
  getDefaultLinkClasses,
  numberToFixed,
  toTitleCase,
};
